import { RENDERER_DEFAULT_OPTIONS, RENDERER_OPTIONS_META } from 'minecraft-renderer/src/graphicsBackend/rendererDefaultOptions';
export const defaultOptions = {
    renderDistance: 3,
    closeConfirmation: true,
    autoFullScreen: false,
    mouseRawInput: true,
    autoExitFullscreen: false,
    localUsername: 'wanderer',
    mouseSensX: 50,
    mouseSensY: -1,
    chatWidth: 320,
    chatHeight: 180,
    chatScale: 100,
    chatOpacity: 100,
    chatOpacityOpened: 100,
    messagesLimit: 200,
    displayLoadingMessages: true,
    volume: 50,
    enableMusic: true,
    musicVolume: 25,
    defaultPerspective: 'first_person',
    guiScale: 3,
    autoRequestCompletions: true,
    touchButtonsSize: 40,
    touchButtonsOpacity: 80,
    touchButtonsPosition: 12,
    touchControlsPositions: getDefaultTouchControlsPositions(),
    touchControlsSize: getTouchControlsSize(),
    touchMovementType: 'modern',
    touchInteractionType: 'classic',
    /** @unstable */
    disableAssets: false,
    /** @unstable */
    debugLogNotFrequentPackets: false,
    unimplementedContainers: false,
    ...RENDERER_DEFAULT_OPTIONS,
    enabledResourcepack: null,
    useVersionsTextures: 'latest',
    serverResourcePacks: 'prompt',
    displayRecordButton: true,
    packetsLoggerPreset: 'all',
    serversAutoVersionSelect: 'auto',
    customChannels: 'websocket',
    remoteContentNotSameOrigin: false,
    packetsRecordingAutoStart: false,
    language: 'auto',
    preciseMouseInput: false,
    // todo ui setting, maybe enable by default?
    waitForChunksRender: false,
    inventoryJei: true,
    // AI Character settings
    aiEnabled: false,
    aiProvider: 'pollinations',
    aiMode: 'creative',
    aiGeminiApiKey: '',
    aiOpenAIApiKey: '',
    aiOpenRouterApiKey: '',
    aiOpenRouterModel: 'openrouter/auto',
    aiResponseLength: 'medium',
    inventoryNotes: true,
    inventoryPlaceholders: false,
    inventoryPlayerModel: true,
    modsSupport: false,
    modsAutoUpdate: 'check',
    modsUpdatePeriodCheck: 24, // hours
    preventBackgroundTimeoutKick: false,
    preventSleep: false,
    debugContro: false,
    debugChatScroll: false,
    chatVanillaRestrictions: true,
    debugResponseTimeIndicator: false,
    chatPingExtension: true,
    chatSpellCheckEnabled: false,
    chatAlwaysDisplayTypingIndicator: false,
    // antiAliasing: false,
    topRightTimeDisplay: 'only-fullscreen',
    singleplayerAutoSave: false,
    alwaysBackupWorldBeforeLoading: undefined,
    alwaysShowMobileControls: false,
    /** Portrait viewport: apply `body.rotated` landscape layout without tapping the rotate button. */
    autoDisplayRotation: false,
    excludeCommunicationDebugEvents: [],
    preventDevReloadWhilePlaying: false,
    localServerOptions: {
        gameMode: 1
    },
    saveLoginPassword: 'prompt',
    preferLoadReadonly: false,
    experimentalClientSelfReload: false,
    remoteSoundsSupport: false,
    remoteSoundsLoadTimeout: 500,
    disableLoadPrompts: false,
    guestUsername: 'guest',
    askGuestName: true,
    errorReporting: true,
    /** Actually might be useful */
    showCursorBlockInSpectator: false,
    chatSelect: true,
    autoOpenAuthModal: false,
    autoJump: 'auto',
    autoParkour: false,
    // advanced bot options
    autoRespawn: false,
    mutedSounds: [],
    plugins: [],
    /** Wether to popup sign editor on server action */
    autoSignEditor: true,
    wysiwygSignEditor: 'auto',
    showMinimap: 'never',
    minimapOptimizations: true,
    displayBossBars: true,
    disabledUiParts: [],
    neighborChunkUpdates: true,
    activeRenderer: 'auto'
};
function getDefaultTouchControlsPositions() {
    return {
        action: [
            70,
            76
        ],
        sneak: [
            84,
            76
        ],
        break: [
            70,
            57
        ],
        jump: [
            84,
            57
        ],
    };
}
function getTouchControlsSize() {
    return {
        joystick: 55,
        action: 36,
        break: 36,
        jump: 36,
        sneak: 36,
    };
}
/**
 * Map of settings that are safe to change from the server.
 * These are rendering/display/input preferences that don't affect critical client functionality.
 * Settings like modsSupport, customChannels, or security-related options are excluded.
 */
export const serverSafeSettings = {
    remoteContentNotSameOrigin: true, // allow server to change remote content not same origin policy
    renderEars: true,
    viewBobbing: true,
    mouseRawInput: true,
    preciseMouseInput: true,
    showHand: true,
    fov: true,
    defaultPerspective: true,
    volume: true,
    musicVolume: true,
    enableMusic: true,
    smoothLighting: true,
    starfieldRendering: true,
    defaultSkybox: true,
    dayCycleAndLighting: true,
    showChunkBorders: true,
    renderDebug: true,
    highlightBlockColor: true,
    displayBossBars: true,
    showMinimap: true,
    autoJump: true,
    chatVanillaRestrictions: true,
    chatPingExtension: true,
    chatSpellCheckEnabled: true,
    renderEntities: true,
    displayRecordButton: true,
    topRightTimeDisplay: true,
    guiScale: true,
    chatWidth: true,
    chatHeight: true,
    chatScale: true,
    loadPlayerSkins: true,
    disableBlockEntityTextures: true,
    neighborChunkUpdates: true,
    newVersionsLighting: true,
    showCursorBlockInSpectator: true,
};
export const optionsMeta = {
    ...RENDERER_OPTIONS_META,
    activeRenderer: {
        possibleValues: [
            ['threejs', 'Three.js (stable)'],
        ]
    },
    serverResourcePacks: {
        possibleValues: ['prompt', 'always', 'never']
    },
    showMinimap: {
        possibleValues: ['always', 'singleplayer', 'never']
    },
    wysiwygSignEditor: {
        possibleValues: ['auto', 'always', 'never']
    },
    touchMovementType: {
        possibleValues: [['modern', 'Modern'], ['classic', 'Classic']]
    },
    touchInteractionType: {
        possibleValues: [['classic', 'Classic'], ['buttons', 'Buttons']]
    },
    autoJump: {
        possibleValues: ['always', 'auto', 'never']
    },
    saveLoginPassword: {
        possibleValues: ['prompt', 'always', 'never']
    },
    packetsLoggerPreset: {
        possibleValues: [
            ['all', 'All'],
            ['no-buffers', 'No Buffers']
        ]
    },
    aiEnabled: {
        possibleValues: [true, false]
    },
    aiProvider: {
        possibleValues: [['pollinations', 'Pollinations (Free)'], ['gemini', 'Google Gemini'], ['openai', 'OpenAI'], ['openrouter', 'OpenRouter']]
    },
    aiMode: {
        possibleValues: [['creative', 'Creative'], ['survival', 'Survival']]
    },
    aiResponseLength: {
        possibleValues: [['short', 'Short'], ['medium', 'Medium'], ['long', 'Long']]
    },
    // Custom string inputs (will use showInputsModal)
    localUsername: {
        isCustomInput: true
    },
    aiGeminiApiKey: {
        isCustomInput: true
    },
    aiOpenAIApiKey: {
        isCustomInput: true
    },
    aiOpenRouterApiKey: {
        isCustomInput: true
    },
    aiOpenRouterModel: {
        isCustomInput: true
    },
    guestUsername: {
        isCustomInput: true
    },
    language: {
        isCustomInput: true
    },
    enabledResourcepack: {
        isCustomInput: true
    },
    useVersionsTextures: {
        isCustomInput: true
    }
};
